/**
 * Legal documents (ADR-0048): /legal/terms | refunds | payouts | prohibited | aml | privacy | cookies | risk | execution.
 * Laid out as a readable document (ADR-0029): the documents and the contents on the side, the text at ~70ch. A section
 * body is plain text: blank lines split paragraphs, "- " lines make a list, a [Bracketed value] is a placeholder still
 * to be filled (highlighted so it cannot be missed), the contact address is a link. Loaded lazily with its texts.
 */
import { cn } from "@prop/ui";
import { Fragment, type ReactNode, useEffect } from "react";
import { Link, useParams } from "wouter";
import { useLegalT } from "../../i18n";
import { LEGAL_DOCS, type LegalDoc, legalSections, useLegalDocsT } from "../../i18n/legalDocs";
import { openConsentSettings } from "../../lib/consent";

const MAIL = "hello@fundedlime.com";
const INLINE = /(\[[^\]]+\]|hello@fundedlime\.com)/;

function inline(text: string): ReactNode[] {
  return text.split(INLINE).map((part, i) => {
    if (part === MAIL)
      return (
        <a key={i} href={`mailto:${MAIL}`} className="underline underline-offset-2 hover:text-foreground">
          {part}
        </a>
      );
    if (/^\[[^\]]+\]$/.test(part))
      return (
        <mark key={i} className="rounded bg-[#d4ff24]/35 px-1 text-foreground">
          {part}
        </mark>
      );
    return <Fragment key={i}>{part}</Fragment>;
  });
}

/** One section body: paragraphs (single line breaks kept) and "- " lists. */
function Body({ text }: { text: string }) {
  return (
    <>
      {text.split(/\n{2,}/).map((block, i) => {
        const lines = block.split("\n");
        if (lines.every((l) => l.startsWith("- ")))
          return (
            <ul key={i} className="flex list-disc flex-col gap-1.5 pl-5 text-[17px] leading-relaxed marker:text-muted-foreground">
              {lines.map((l, j) => (
                <li key={j}>{inline(l.slice(2))}</li>
              ))}
            </ul>
          );
        return (
          <p key={i} className="text-[17px] leading-relaxed">
            {lines.map((l, j) => (
              <Fragment key={j}>
                {j > 0 && <br />}
                {inline(l)}
              </Fragment>
            ))}
          </p>
        );
      })}
    </>
  );
}

export function LegalPage() {
  const t = useLegalT();
  const td = useLegalDocsT();
  const params = useParams<{ doc: string }>();
  const doc: LegalDoc = (LEGAL_DOCS as readonly string[]).includes(params.doc) ? (params.doc as LegalDoc) : "terms";
  const sections = legalSections(doc);
  const heading = (n: string) => td(`${doc}.s${n}h` as "terms.s1h");
  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
  }, [doc]);
  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6">
      <div className="grid gap-8 lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-14">
        <aside className="flex flex-col gap-8 lg:sticky lg:top-20 lg:max-h-[calc(100dvh-6rem)] lg:self-start lg:overflow-y-auto">
          <nav aria-label={t("documents")} className="flex flex-wrap gap-1.5 lg:flex-col lg:items-start lg:gap-1">
            {LEGAL_DOCS.map((d) => (
              <Link
                key={d}
                href={`/legal/${d}`}
                aria-current={d === doc ? "page" : undefined}
                className={cn(
                  "rounded-full px-3.5 py-1.5 text-sm transition-colors duration-200",
                  d === doc ? "bg-foreground font-medium text-background" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                {t(`nav.${d}`)}
              </Link>
            ))}
          </nav>
          <nav aria-labelledby="toc-h" className="hidden flex-col gap-2 border-t border-border pt-6 lg:flex">
            <p id="toc-h" className="text-sm font-semibold">
              {t("contents")}
            </p>
            <ol className="flex flex-col gap-1.5 text-sm">
              {sections.map((n) => (
                <li key={n}>
                  <a href={`#s${n}`} className="flex gap-2 text-muted-foreground hover:text-foreground">
                    <span className="tabular-nums">{n}.</span>
                    {heading(n)}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        </aside>
        <article className="flex min-w-0 max-w-[70ch] flex-col gap-8">
          <header className="flex flex-col gap-3">
            <h1>{td(`${doc}.title`)}</h1>
            <p className="text-lg leading-relaxed text-muted-foreground">{td(`${doc}.lead`)}</p>
            <p className="text-sm text-muted-foreground">{t("updated")}</p>
          </header>
          {sections.map((n) => (
            <section key={n} id={`s${n}`} aria-labelledby={`s${n}-h`} className="flex scroll-mt-20 flex-col gap-3 border-t border-border pt-6">
              <h2 id={`s${n}-h`} className="flex gap-3 text-lg font-semibold">
                <span className="tabular-nums text-muted-foreground">{n}</span>
                {heading(n)}
              </h2>
              <Body text={td(`${doc}.s${n}p` as "terms.s1p")} />
            </section>
          ))}
          <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-6 text-sm text-muted-foreground">
            <span>{inline(t("questions"))}</span>
            <button type="button" onClick={openConsentSettings} className="underline underline-offset-2 hover:text-foreground">
              {t("consent.open")}
            </button>
          </footer>
        </article>
      </div>
    </main>
  );
}
